type AnalyticsProps = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __konsulentAnalyticsInitialized?: boolean;
  }
}

const ANALYTICS_APP_NAME = (import.meta.env.VITE_ANALYTICS_APP_NAME ?? 'konsulent').trim();
const APP_ENV = import.meta.env.MODE;
const GA_MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID ?? '').trim();

const appendScriptOnce = (id: string, src: string): void => {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  document.head.appendChild(script);
};

const getBaseProps = (): AnalyticsProps => ({
  app: ANALYTICS_APP_NAME,
  env: APP_ENV,
});

export const initializeAnalytics = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__konsulentAnalyticsInitialized) return;
  window.__konsulentAnalyticsInitialized = true;

  if (!GA_MEASUREMENT_ID) return;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag =
    window.gtag ??
    ((...args: unknown[]) => {
      window.dataLayer?.push(args);
    });

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });

  appendScriptOnce(
    'ga4-script',
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`
  );
};

export const trackPageView = (pathname: string): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!GA_MEASUREMENT_ID || typeof window.gtag !== 'function') return;

  const pagePath = pathname || '/';
  const pageLocation = window.location.href;

  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_location: pageLocation,
    page_title: document.title,
    ...getBaseProps(),
  });
};

export const trackEvent = (eventName: string, properties: AnalyticsProps = {}): void => {
  if (typeof window === 'undefined') return;
  if (!GA_MEASUREMENT_ID || typeof window.gtag !== 'function') return;

  window.gtag('event', eventName, {
    ...getBaseProps(),
    ...properties,
  });
};

export {};
